import React, { useMemo, useState } from 'react';

import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import {
  Employee,
  employeeCoordinate,
  TaskStatusCoordinateDetails,
  TaskStatusTransitionCoordinate
} from 'types/interfaces';
import { formatDateStringFromRaw } from 'utils/date-utils';

import TaskStatusMap from './TaskStatusMap';

const SIDE_PANEL_WIDTH = 340;

interface TaskMapDialogProps {
  visible: boolean;
  loading: boolean;
  error?: string | null;
  onHide: () => void;
  taskDetails?: TaskStatusCoordinateDetails | null;
}

const TaskMapDialog: React.FC<TaskMapDialogProps> = ({
  visible,
  loading,
  error,
  onHide,
  taskDetails
}) => {
  const [showEmployeesDialog, setShowEmployeesDialog] = useState(false);
  const [employeesForDialog, setEmployeesForDialog] = useState<Employee[]>([]);

  const handleShowEmployees = (employees: Employee[]) => {
    setEmployeesForDialog(employees);
    setShowEmployeesDialog(true);
  };

  const orderedTransitions = useMemo(() => {
    const transitions = taskDetails?.transitions ?? [];

    return transitions
      .filter((transition) => transition.visibleOnMap !== false)
      .slice()
      .sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      )
      .map((transition, index) => ({
        ...transition,
        order: index + 1
      }));
  }, [taskDetails]);

  const transitionsWithCoordinates = useMemo(
    () =>
      orderedTransitions.filter(
        (transition): transition is TaskStatusTransitionCoordinate & {
          order: number;
          latitude: number;
          longitude: number;
        } =>
          transition.visibleOnMap !== false &&
          typeof transition.latitude === 'number' &&
          typeof transition.longitude === 'number'
      ),
    [orderedTransitions]
  );

  const transitionsWithoutCoordinates = useMemo(
    () =>
      orderedTransitions.filter(
        (transition) =>
          transition.visibleOnMap !== false &&
          (transition.latitude === undefined ||
          transition.longitude === undefined)
      ),
    [orderedTransitions]
  );

  const employeesWithCoordinates = useMemo(
    () =>
      taskDetails?.employees?.filter(
        (employee): employee is employeeCoordinate & {
          latitude: number;
          longitude: number;
        } =>
          typeof employee.latitude === 'number' &&
          typeof employee.longitude === 'number' &&
          (() => {
            if (!employee.lastCoordinateUpdate) return false;
            const updateTime = new Date(employee.lastCoordinateUpdate).getTime();
            const now = Date.now();
            const hoursDiff = (now - updateTime) / (1000 * 60 * 60);
            return hoursDiff <= 24;
          })()
      ),
    [taskDetails]
  );

  const employeesWithoutCoordinates = useMemo(
    () =>
      taskDetails?.employees?.filter(
        (employee) => {
          if (
            employee.latitude === undefined ||
            employee.longitude === undefined
          ) {
            return true;
          }
          if (!employee.lastCoordinateUpdate) {
            return true;
          }
          const updateTime = new Date(employee.lastCoordinateUpdate).getTime();
          const now = Date.now();
          const hoursDiff = (now - updateTime) / (1000 * 60 * 60);
          return hoursDiff > 24;
        }
      ),
    [taskDetails]
  );

  const isMobile =
    typeof window !== 'undefined' && window.innerWidth <= 768;

  const hasTaskCoordinates = Boolean(
    taskDetails?.asset?.latitude && taskDetails?.asset?.longitude
  );

  const showSidePanel =
    transitionsWithoutCoordinates.length > 0 ||
    !hasTaskCoordinates ||
    employeesWithoutCoordinates!.length > 0;

  const mapContainerId = useMemo(
    () => `task-map-container-${taskDetails?.id ?? 'new'}`,
    [taskDetails?.id]
  );

  const renderMissingTransition = (
    transition: TaskStatusTransitionCoordinate & { order: number }
  ) => (
    <div
      key={`${transition.statusesTransitionId}-${transition.order}`}
      style={{
        borderRadius: '12px',
        border: '1px solid #d1d5db',
        padding: '12px',
        background: '#ffffff',
        boxShadow: '0 2px 6px rgba(15, 23, 42, 0.08)',
        marginBottom: '12px'
      }}
    >
      <div
        style={{
          fontSize: '12px',
          fontWeight: 600,
          color: '#0ea5e9',
          marginBottom: '8px'
        }}
      >
        Переход №{transition.order}
      </div>
      <div>
        <strong>Статус до:</strong> {transition.fromStatus?.label || '-'}
      </div>
      <div>
        <strong>Статус после:</strong> {transition.toStatus?.label || '-'}
      </div>
      <div>
        <strong>Дата:</strong> {formatDateStringFromRaw(transition.date)}
      </div>
      {transition.createdBy?.label && (
        <div>
          <strong>Исполнитель:</strong> {transition.createdBy.label}
        </div>
      )}
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Карта наряда"
      className="task-map-dialog"
      style={{ width: '96vw', maxWidth: '1500px', height: '700px' }}
      contentStyle={{ padding: 0, height: 'calc(100% - 50px)' }}
      breakpoints={{ '1200px': '98vw', '768px': '100vw' }}
    >
      {loading ? (
        <div className="flex justify-content-center py-5">
          <ProgressSpinner />
        </div>
      ) : error ? (
        <Message severity="error" text={error} />
      ) : !taskDetails ? (
        <Message severity="warn" text="Нет данных для отображения карты." />
      ) : (
        <div
          className="flex flex-column"
          style={{
            height: '100%',
            background: '#f8fafc'
          }}
        >
          <div
            className="flex task-map-dialog__layout"
            style={{
              width: '100%',
              gap: showSidePanel ? '0' : 0,
              height: '100%'
            }}
          >
            <div
              className="task-map-dialog__map"
              style={{
                flex: 1,
                width: '100%',
                minHeight: '560px',
                padding: '20px',
                paddingBottom: '20px',
                height: '100%'
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  background: '#fff',
                  boxShadow: '0 10px 25px rgba(15, 23, 42, 0.1)'
                }}
              >
                <TaskStatusMap
                  taskDetails={taskDetails}
                  transitionsWithCoordinates={transitionsWithCoordinates}
                  employeesWithCoordinates={employeesWithCoordinates as employeeCoordinate[]}
                  containerId={mapContainerId}
                  reservedRightWidth={
                    showSidePanel && !isMobile ? SIDE_PANEL_WIDTH : 0
                  }
                  onShowEmployees={handleShowEmployees}
                />
              </div>
            </div>

            {showSidePanel && (
              <div
                className="task-map-dialog__side"
                style={{
                  width: `${SIDE_PANEL_WIDTH}px`,
                  padding: '20px 20px 20px 0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  height: '100%'
                }}
              >
                {transitionsWithoutCoordinates.length > 0 && (
                  <div
                    style={{
                      background: '#fff',
                      borderRadius: '16px',
                      padding: '16px',
                      boxShadow: '0 10px 25px rgba(15, 23, 42, 0.1)',
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      minHeight: 0
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        marginBottom: '12px',
                        fontSize: '15px'
                      }}
                    >
                      Нет координат переходов статусов
                    </h4>
                    <div
                      style={{
                        overflowY: 'auto',
                        paddingRight: '8px',
                        flex: 1,
                        minHeight: 0
                      }}
                    >
                      {transitionsWithoutCoordinates.map((transition) =>
                        renderMissingTransition(transition)
                      )}
                    </div>
                  </div>
                )}

                {!hasTaskCoordinates && (
                  <div
                    style={{
                      background: '#fff',
                      borderRadius: '16px',
                      padding: '16px',
                      boxShadow: '0 10px 25px rgba(15, 23, 42, 0.1)'
                    }}
                  >
                    <h4 style={{ marginTop: 0, marginBottom: '8px' }}>
                      Нет координат наряда
                    </h4>
                    <p style={{ marginBottom: 0, fontSize: '14px' }}>
                      Для наряда №{taskDetails.id} отсутствуют координаты
                      объекта. На карте показываются только переходы статусов.
                    </p>
                  </div>
                )}

                {employeesWithoutCoordinates!.length > 0 && (
                  <div
                    style={{
                      background: '#fff',
                      borderRadius: '16px',
                      padding: '16px',
                      boxShadow: '0 10px 25px rgba(15, 23, 42, 0.1)',
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      minHeight: 0
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        marginBottom: '12px',
                        fontSize: '15px'
                      }}
                    >
                      Отсутствуют координаты исполнителей
                    </h4>
                    <div
                      style={{
                        overflowY: 'auto',
                        paddingRight: '8px',
                        flex: 1,
                        minHeight: 0
                      }}
                    >
                      {employeesWithoutCoordinates!.map((employee) => (
                        <div
                          key={employee.id || employee.fullName}
                          style={{
                            borderRadius: '12px',
                            border: '1px solid #d1d5db',
                            padding: '12px',
                            background: '#ffffff',
                            boxShadow: '0 2px 6px rgba(15, 23, 42, 0.08)',
                            marginBottom: '12px'
                          }}
                        >
                          <div
                            style={{
                              fontSize: '14px',
                              fontWeight: 500,
                              color: '#0f172a'
                            }}
                          >
                            {employee.fullName || '—'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <Dialog
        visible={showEmployeesDialog}
        onHide={() => setShowEmployeesDialog(false)}
        header="Сотрудники"
        style={{ width: '420px' }}
      >
        <ul style={{ paddingLeft: '20px', margin: 0 }}>
          {employeesForDialog.map((employee) => (
            <li key={employee.id} style={{ marginBottom: '6px' }}>
              {employee.fullName}
            </li>
          ))}
        </ul>
      </Dialog>
    </Dialog>
  );
};

export default TaskMapDialog;

